import { useState } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { X, Loader2, CreditCard, User, Package, Truck, Globe, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/Tooltip";
import api from "../services/api";
import { useCart } from "./CartContext";
import { toast } from "sonner";

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
}

export function CheckoutModal({ isOpen, onClose, total }: CheckoutModalProps) {
  const { items, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [showCepSearch, setShowCepSearch] = useState(false);
  const [cepSearchResults, setCepSearchResults] = useState<any[]>([]);
  const [searchingCep, setSearchingCep] = useState(false);

  const [cepSearchData, setCepSearchData] = useState({
    uf: "",
    cidade: "",
    logradouro: ""
  });

  const [formData, setFormData] = useState({
    cpfCnpj: "",
    nome: "",
    sobrenome: "",
    email: "",
    celular: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    formaPagamento: "2",
    incluiFrete: true,
    isPickup: false,
    country: "BR",
    countryCode: "+55",
    intlCountry: "",
    discount: "",
    discountCode: "",
    pagamentos: [
      { valor: "", tipo: "" },
      { valor: "", tipo: "" },
      { valor: "", tipo: "" },
      { valor: "", tipo: "" }
    ]
  });

  const handleCpfCnpjBlur = async () => {
    if (formData.cpfCnpj.length >= 11) {
      setSearching(true);
      try {
        const response = await api.post('/api/admin/users/find-by-document', {
          document: formData.cpfCnpj
        });
        const user = response.data.data;

        setIsExistingCustomer(true);
        setFormData(prev => ({
          ...prev,
          nome: user.first_name || prev.nome,
          sobrenome: user.last_name || prev.sobrenome,
          email: user.email || prev.email,
          celular: user.phone || prev.celular,
          cep: user.address?.zip || prev.cep,
          endereco: user.address?.address || prev.endereco,
          numero: user.address?.number || prev.numero,
          complemento: user.address?.complement ?? "",
          bairro: user.address?.neighborhood || prev.bairro,
          cidade: user.address?.city || prev.cidade,
          estado: user.address?.state || prev.estado,
        }));
        toast.success("Cliente recorrente identificado!");
      } catch (error: any) {
        if (error.response?.status === 404) {
          setIsExistingCustomer(false);
          // Resetar campos se não encontrar o cliente
          setFormData(prev => ({
            ...prev,
            nome: "", sobrenome: "", email: "", celular: "", cep: "",
            endereco: "", numero: "", complemento: "", bairro: "",
            cidade: "", estado: ""
          }));
          toast.info("Novo cliente. Preencha os dados.");
        } else {
          toast.error("Erro ao buscar cliente por CPF/CNPJ");
        }
      } finally {
        setSearching(false);
      }
    } else if (formData.cpfCnpj.length > 0 && formData.cpfCnpj.length < 11) {
      // Se tiver algo digitado mas for curto demais, também resetamos o estado de cliente existente
      setIsExistingCustomer(false);
    } else if (formData.cpfCnpj.length === 0) {
      setIsExistingCustomer(false);
      // Resetar form se apagar o CPF
      setFormData(prev => ({
        ...prev,
        nome: "", sobrenome: "", email: "", celular: "", cep: "",
        endereco: "", numero: "", complemento: "", bairro: "",
        cidade: "", estado: ""
      }));
    }
  };
  const handleCepChange = async (value: string) => {
    const cleanCep = value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, cep: cleanCep.substring(0, 8) }));

    if (cleanCep.length === 8) {
      try {
        const response = await api.get(`/api/cep/${cleanCep}`);
        const data = response.data;

        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.uf
          }));
        } else {
          toast.error("CEP não encontrado");
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };


  const formatBRPhone = (v: string) => {
    const d = v.replace(/\D/g, "").substring(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 7) return `(${d.substring(0, 2)}) ${d.substring(2)}`;
    return `(${d.substring(0, 2)}) ${d.substring(2, 7)}-${d.substring(7)}`;
  };

  const handlePhoneChange = (value: string) => {
    if (formData.country === "BR") {
      const digits = value.replace(/\D/g, "");
      setFormData(prev => ({ ...prev, celular: formatBRPhone(digits) }));
    } else {
      setFormData(prev => ({ ...prev, celular: value }));
    }
  };

  const handleCountryChange = (country: string) => {
    const code = country === "BR" ? "+55" : "";
    setFormData({
      ...formData,
      country,
      countryCode: code,
      intlCountry: country === "BR" ? "Brasil" : "",
      cep: "",
      endereco: "",
      bairro: "",
      cidade: "",
      estado: "",
      celular: "",
      cpfCnpj: ""
    });
    setIsExistingCustomer(false);
  };

  const handleSearchCepByAddress = async () => {
    if (!cepSearchData.uf || !cepSearchData.cidade || !cepSearchData.logradouro) {
      toast.error("Preencha UF, Cidade e Rua para buscar");
      return;
    }

    if (cepSearchData.cidade.length < 3 || cepSearchData.logradouro.length < 3) {
      toast.error("Cidade e Rua precisam de pelo menos 3 caracteres");
      return;
    }

    setSearchingCep(true);
    try {
      const response = await api.get(`/api/cep/${cepSearchData.uf}/${cepSearchData.cidade}/${cepSearchData.logradouro}`);
      const data = response.data;

      if (Array.isArray(data) && data.length > 0) {
        setCepSearchResults(data);
      } else {
        toast.error("Nenhum CEP encontrado para este endereço");
        setCepSearchResults([]);
      }
    } catch (error) {
      toast.error("Erro na busca de CEP");
    } finally {
      setSearchingCep(false);
    }
  };

  const selectAddressFromSearch = (addr: any) => {
    setFormData(prev => ({
      ...prev,
      cep: addr.cep.replace(/\D/g, ''),
      endereco: addr.logradouro,
      bairro: addr.bairro,
      cidade: addr.localidade,
      estado: addr.uf
    }));
    setShowCepSearch(false);
    setCepSearchResults([]);
    setCepSearchData({ uf: "", cidade: "", logradouro: "" });
  };

  const parseAmount = (val: string | number) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const normalized = val.toString().trim().replace(',', '.');
    return parseFloat(normalized) || 0;
  };

  const handlePagamentoChange = (index: number, field: 'valor' | 'tipo', value: string) => {
    const newPagamentos = [...formData.pagamentos];
    let cleanValue = value;
    if (field === 'valor') {
      // Permite APENAS números e vírgula (sem ponto)
      cleanValue = value.replace(/[^0-9,]/g, '');
      const parts = cleanValue.split(',');
      if (parts.length > 2) {
        cleanValue = parts[0] + ',' + parts.slice(1).join('');
      }
    }
    newPagamentos[index] = { ...newPagamentos[index], [field]: cleanValue };
    setFormData({ ...formData, pagamentos: newPagamentos });
  };

  // Calcula frete total se aplicável
  const shippingTotal = (formData.incluiFrete && !formData.isPickup)
    ? items.reduce((sum, item) => {
      const sPrice = parseFloat(item.shippingPrice.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
      return sum + sPrice;
    }, 0)
    : 0;

  const discountVal = parseFloat(formData.discount) || 0;
  const expectedTotal = Math.max(total + shippingTotal - discountVal, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("O carrinho está vazio");
    if (!formData.nome || !formData.email || !formData.cpfCnpj) return toast.error("Preencha os dados do cliente");

    // Validação de casas decimais após a vírgula nas formas de pagamento
    for (const p of formData.pagamentos) {
      if (p.valor.includes(',')) {
        const decimals = p.valor.split(',')[1] || '';
        if (decimals.length > 2) {
          toast.error(`O valor "R$ ${p.valor}" possui mais de 2 casas decimais após a vírgula. Por favor, corrija os centavos.`);
          return;
        }
      }
    }

    const totalPayments = formData.pagamentos.reduce((sum, p) => sum + parseAmount(p.valor), 0);

    // Validações de "Realidade" para Internacional
    if (formData.country === "INT") {
      const BRAZILIAN_STATES = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

      if (!formData.intlCountry) {
        toast.error("Por favor, informe o país de destino.");
        return;
      }

      if (BRAZILIAN_STATES.includes(formData.estado.toUpperCase())) {
        toast.error("Você selecionou Internacional, mas usou um Estado do Brasil. Por favor, use a aba BRASIL para garantir a integridade dos dados.");
        return;
      }

      if (formData.countryCode === "+55") {
        toast.error("O código +55 é exclusivo para o Brasil. Use a aba BRASIL.");
        return;
      }

      if (formData.cep.length < 3) {
        toast.error("Por favor, insira um Zip Code válido.");
        return;
      }
    }

    if (totalPayments > 0 && Math.abs(totalPayments - expectedTotal) > 0.01) {
      toast.error(`Soma dos pagamentos (R$ ${totalPayments.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) diferente do esperado (R$ ${expectedTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).`);
      return;
    }

    setLoading(true);
    try {
      // Prepara os dados para o envio (limpeza e unificação)
      const submitData = {
        ...formData,
        // Garante que o telefone vá com o código do país
        celular: `${formData.countryCode} ${formData.celular.replace(/\D/g, '')}`.trim(),
        // Normaliza o país para o backend
        pais: formData.country === "BR" ? "Brasil" : formData.intlCountry,
      };

      await api.post('/api/admin/orders', {
        customer: submitData,
        items: items.map(item => ({ id: item.id, quantity: item.quantity, paymentType: item.paymentType })),
        payment_method: formData.formaPagamento,
        pagamentos: formData.pagamentos,
        include_shipping: formData.isPickup ? false : formData.incluiFrete,
        is_pickup: formData.isPickup,
        total: expectedTotal,
        discount: parseFloat(formData.discount) || 0,
        discount_code: parseFloat(formData.discount) > 0 ? formData.discountCode : null
      });
      toast.success("Venda realizada com sucesso!");
      clearCart();
      onClose();
      // Recarrega a página para atualizar o status dos produtos vendidos
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erro ao processar venda");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="bg-[#D4C070] p-2 rounded-lg">
              <CreditCard className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Finalizar Venda</h2>
              <p className="text-sm text-gray-500">Fluxo administrativo seguro para pedidos</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
            <X className="h-6 w-6 text-gray-400" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-8 space-y-8">
            {/* Seção 1: Cliente */}
            <div>
              <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                <User className="h-5 w-5 text-[#D4C070]" />
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Identificação do Cliente</h3>
              </div>

              {/* Seleção de País */}
              <div className="mb-6 flex gap-4 p-1 bg-gray-100 rounded-lg w-fit">
                <button
                  type="button"
                  onClick={() => handleCountryChange("BR")}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${formData.country === "BR" ? "bg-white text-[#D4C070] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  BRASIL
                </button>
                <button
                  type="button"
                  onClick={() => handleCountryChange("INT")}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${formData.country === "INT" ? "bg-white text-[#D4C070] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  INTERNACIONAL
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {formData.country === "INT" && (
                  <div className="space-y-2 md:col-span-3">
                    <Label htmlFor="intlCountry" className="text-xs font-bold uppercase text-gray-500">País de Destino</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="intlCountry"
                        placeholder="Ex: Estados Unidos, Portugal, França..."
                        value={formData.intlCountry}
                        onChange={e => setFormData({ ...formData, intlCountry: e.target.value })}
                        className="bg-gray-50 border-gray-200 pl-10 focus:ring-[#D4C070]"
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="cpfCnpj" className="text-xs font-bold uppercase text-gray-500">
                    {formData.country === "BR" ? "CPF / CNPJ" : "Documento (ID / Passaporte)"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="cpfCnpj"
                      placeholder={formData.country === "BR" ? "Somente números" : "Número do documento"}
                      value={formData.cpfCnpj}
                      onChange={e => setFormData({ ...formData, cpfCnpj: formData.country === "BR" ? e.target.value.replace(/\D/g, '') : e.target.value })}
                      onBlur={formData.country === "BR" ? handleCpfCnpjBlur : undefined}
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                    />
                    {searching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-[#D4C070]" />
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formData.country === "BR"
                      ? "ℹ️ O preenchimento do CPF/CNPJ busca automaticamente os dados de clientes já cadastrados."
                      : "Informe o documento de identificação internacional."
                    }
                  </p>
                  {isExistingCustomer && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Cliente Recorrente (Endereço Protegido)
                    </span>
                  )}
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="nome" className="text-xs font-bold uppercase text-gray-500">Nome Completo</Label>
                  <div className="flex gap-2">
                    <Input
                      id="nome"
                      placeholder="Nome"
                      value={formData.nome}
                      onChange={e => setFormData({ ...formData, nome: e.target.value })}
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                    />
                    <Input
                      id="sobrenome"
                      placeholder="Sobrenome"
                      value={formData.sobrenome}
                      onChange={e => setFormData({ ...formData, sobrenome: e.target.value })}
                      className="bg-gray-50 border-gray-200 focus:ring-[#D4C070]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase text-gray-500">E-mail</Label>
                  <Input id="email" type="email" placeholder="email@exemplo.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="celular" className="text-xs font-bold uppercase text-gray-500">Celular</Label>
                  <div className="flex gap-2">
                    <div className="w-20">
                      <Input
                        placeholder="+XX"
                        value={formData.countryCode}
                        onChange={e => setFormData({ ...formData, countryCode: e.target.value })}
                        className="bg-gray-50 border-gray-200 text-center font-bold"
                      />
                    </div>
                    <Input
                      id="celular"
                      placeholder={formData.country === "BR" ? "(00) 00000-0000" : "Número do telefone"}
                      value={formData.celular}
                      onChange={e => handlePhoneChange(e.target.value)}
                      className="bg-gray-50 border-gray-200 flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="cep" className="text-xs font-bold uppercase text-gray-500">
                        {formData.country === "BR" ? "CEP" : "ZIP CODE"}
                      </Label>
                      {isExistingCustomer && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-[#D4C070] cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-[10px]">O CEP permanece editável para que você possa atualizar ou corrigir o endereço do cliente se necessário.</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {formData.country === "BR" && !isExistingCustomer && (
                      <button
                        type="button"
                        onClick={() => setShowCepSearch(!showCepSearch)}
                        className="text-[10px] text-[#D4C070] font-bold hover:underline uppercase"
                      >
                        {showCepSearch ? "Voltar ao CEP" : "Não sei o CEP"}
                      </button>
                    )}
                  </div>
                  <Input
                    id="cep"
                    placeholder={formData.country === "BR" ? "00000000" : "Zip Code"}
                    value={formData.cep}
                    onChange={e => formData.country === "BR" ? handleCepChange(e.target.value) : setFormData({ ...formData, cep: e.target.value })}
                    className={`bg-white border-gray-200 focus:ring-[#D4C070] ${isExistingCustomer ? "border-[#D4C070] ring-1 ring-[#D4C070]/20" : ""}`}
                    readOnly={false}
                  />
                </div>
              </div>

              {/* Busca de CEP por Rua */}
              {showCepSearch && !isExistingCustomer && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-[#D4C070]/20 space-y-4">
                  <p className="text-xs font-bold text-[#D4C070] uppercase">Busca por Logradouro</p>
                  <div className="grid grid-cols-4 gap-3">
                    <Input placeholder="UF" maxLength={2} value={cepSearchData.uf} onChange={e => setCepSearchData({ ...cepSearchData, uf: e.target.value.toUpperCase() })} className="bg-white uppercase" />
                    <Input placeholder="Cidade" value={cepSearchData.cidade} onChange={e => setCepSearchData({ ...cepSearchData, cidade: e.target.value })} className="bg-white col-span-1" />
                    <Input placeholder="Rua / Logradouro" value={cepSearchData.logradouro} onChange={e => setCepSearchData({ ...cepSearchData, logradouro: e.target.value })} className="bg-white col-span-2" />
                  </div>
                  <Button type="button" onClick={handleSearchCepByAddress} disabled={searchingCep} className="w-full bg-white border border-[#D4C070] text-[#D4C070] hover:bg-[#D4C070]/10 text-xs font-bold">
                    {searchingCep ? <Loader2 className="h-4 w-4 animate-spin" /> : "BUSCAR CEP"}
                  </Button>

                  {cepSearchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-2 mt-2 custom-scrollbar pr-2">
                      {cepSearchResults.map((addr, i) => (
                        <div
                          key={i}
                          onClick={() => selectAddressFromSearch(addr)}
                          className="p-2 bg-white border border-gray-100 rounded hover:border-[#D4C070] cursor-pointer text-[10px]"
                        >
                          <p className="font-bold">{addr.logradouro}</p>
                          <p className="text-gray-500">{addr.bairro} - {addr.localidade}/{addr.uf} - {addr.cep}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="endereco" className="text-xs font-bold uppercase text-gray-500">Endereço</Label>
                  <Input id="endereco" value={formData.endereco} onChange={e => setFormData({ ...formData, endereco: e.target.value })} className="bg-gray-50 border-gray-200" readOnly={isExistingCustomer} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero" className="text-xs font-bold uppercase text-gray-500">Número</Label>
                  <Input id="numero" value={formData.numero} onChange={e => setFormData({ ...formData, numero: e.target.value })} className="bg-white border-gray-200" readOnly={false} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento" className="text-xs font-bold uppercase text-gray-500">Complemento</Label>
                  <Input id="complemento" value={formData.complemento} onChange={e => setFormData({ ...formData, complemento: e.target.value })} className="bg-white border-gray-200" readOnly={false} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="bairro" className="text-xs font-bold uppercase text-gray-500">Bairro</Label>
                  <Input id="bairro" value={formData.bairro} onChange={e => setFormData({ ...formData, bairro: e.target.value })} className="bg-gray-50 border-gray-200" readOnly={isExistingCustomer} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade" className="text-xs font-bold uppercase text-gray-500">Cidade</Label>
                  <Input id="cidade" value={formData.cidade} onChange={e => setFormData({ ...formData, cidade: e.target.value })} className="bg-gray-50 border-gray-200" readOnly={isExistingCustomer} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado" className="text-xs font-bold uppercase text-gray-500">Estado (UF)</Label>
                  <Input id="estado" value={formData.estado} onChange={e => setFormData({ ...formData, estado: e.target.value.toUpperCase() })} maxLength={2} className="bg-gray-50 border-gray-200 uppercase" readOnly={isExistingCustomer} />
                </div>
              </div>
            </div>

            {/* Seção 2: Pagamento e Logística */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <Package className="h-5 w-5 text-[#D4C070]" />
                  <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Logística e Frete</h3>
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-4">
                    <div className="flex items-center justify-between group cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, isPickup: !prev.isPickup }))}>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg transition-colors ${formData.isPickup ? 'bg-[#D4C070] text-white' : 'bg-white text-gray-400'}`}>
                          <Truck className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Retirada em Loja</p>
                          <p className="text-xs text-gray-500">Sem custos de envio</p>
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full p-1 transition-colors ${formData.isPickup ? 'bg-[#D4C070]' : 'bg-gray-300'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${formData.isPickup ? 'translate-x-6' : 'translate-x-0'}`} />
                      </div>
                    </div>

                    {!formData.isPickup && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200" onClick={() => setFormData(prev => ({ ...prev, incluiFrete: !prev.incluiFrete }))}>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg transition-colors ${formData.incluiFrete ? 'bg-[#849D70] text-white' : 'bg-white text-gray-400'}`}>
                            <CreditCard className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">Pagamento inclui frete</p>
                            <p className="text-xs text-gray-500">Somar valor do frete ao total</p>
                          </div>
                        </div>
                        <div className={`w-12 h-6 rounded-full p-1 transition-colors ${formData.incluiFrete ? 'bg-[#849D70]' : 'bg-gray-300'}`}>
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${formData.incluiFrete ? 'translate-x-6' : 'translate-x-0'}`} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase text-gray-500">Meio de Pagamento Principal</Label>
                    <select
                      value={formData.formaPagamento}
                      onChange={e => setFormData({ ...formData, formaPagamento: e.target.value })}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-[#68686A]"
                    >
                      <option value="2">Cartão de Crédito</option>
                      <option value="3">Dinheiro</option>
                      <option value="4">Paypal (link)</option>
                      <option value="5">Depósito</option>
                      <option value="6">Dinheiro + Depósito</option>
                      <option value="7">Dinheiro + Cartão</option>
                      <option value="8">Dinheiro + Cheque</option>
                      <option value="9">Depósito + Cartão (Loja)</option>
                      <option value="10">Crédito Cliente (Loja)</option>
                      <option value="11">Crédito Fornecedora (Loja)</option>
                      <option value="12">Depósito + link PayPal</option>
                      <option value="13">Desconto / Fora do Closet</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discount" className="text-xs font-bold uppercase text-gray-500">Desconto (R$)</Label>
                    <Input
                      id="discount"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0,00"
                      value={formData.discount}
                      onChange={e => {
                        const val = e.target.value;
                        const isGreaterThanZero = parseFloat(val) > 0;
                        setFormData({
                          ...formData,
                          discount: val,
                          discountCode: isGreaterThanZero && !formData.discountCode ? 'admin_cash_payment' : (isGreaterThanZero ? formData.discountCode : '')
                        });
                      }}
                      className="bg-white border-gray-200 focus:ring-[#D4C070]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discountCode" className="text-xs font-bold uppercase text-gray-500">Motivo do Desconto</Label>
                    <select
                      id="discountCode"
                      value={formData.discountCode}
                      onChange={e => setFormData({ ...formData, discountCode: e.target.value })}
                      disabled={!formData.discount || parseFloat(formData.discount) <= 0}
                      className="w-full h-10 px-3 bg-white border border-gray-200 rounded-md text-sm text-[#68686A] disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">Selecione...</option>
                      <option value="admin_cash_payment">Desconto Pagamento Ato</option>
                      <option value="admin_free_shipping">Frete Grátis</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                  <CreditCard className="h-5 w-5 text-[#D4C070]" />
                  <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wider">Divisão de Pagamentos</h3>
                </div>

                <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] font-black tracking-widest">
                      <tr>
                        <th className="px-4 py-3 text-left">Valor (R$)</th>
                        <th className="px-4 py-3 text-left">Tipo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {formData.pagamentos.map((p, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <Input
                              placeholder="0"
                              value={p.valor}
                              onChange={e => handlePagamentoChange(index, 'valor', e.target.value)}
                              className="h-8 text-right font-bold border-gray-200"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={p.tipo}
                              onChange={e => handlePagamentoChange(index, 'tipo', e.target.value)}
                              className="w-full h-8 px-2 border border-gray-200 rounded text-[10px] text-[#68686A]"
                            >
                              <option value="">Selecione...</option>
                              <option value="TED/PIX">TED/PIX</option>
                              <option value="Espécie">Espécie</option>
                              <option value="Rede">Rede</option>
                              <option value="Paypal">Paypal</option>
                              <option value="Crédito Fornecedora">Crédito Fornecedora</option>
                              <option value="Crédito Cliente">Crédito Cliente</option>
                              <option value="Depósito">Depósito</option>
                              <option value="Desconto / Fora do Closet">Desconto / Fora do Closet</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Resumo e Botão Salvar */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/30">
              <div className="flex flex-col gap-2 mb-4 p-2 bg-white rounded border border-gray-100">
                <div className="flex justify-between text-sm text-[#68686A]">
                  <span>Subtotal:</span>
                  <span>R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                {(formData.incluiFrete && !formData.isPickup) && (
                  <div className="flex justify-between text-sm text-[#68686A]">
                    <span>Frete:</span>
                    <span>R$ {items.reduce((sum, item) => sum + (parseFloat(item.shippingPrice.replace(/[R$\s.]/g, '').replace(',', '.')) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {discountVal > 0 && (
                  <div className="flex justify-between text-sm text-red-500 font-semibold">
                    <span>Desconto:</span>
                    <span>- R$ {discountVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg text-gray-900 border-t pt-2 mt-1">
                  <span>Total Esperado:</span>
                  <span className="text-[#849D70]">
                    R$ {expectedTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || searching}
                className="w-full bg-[#D4C070] hover:bg-[#849D70] text-white h-12 font-bold text-lg shadow-lg shadow-[#D4C070]/20 rounded-xl"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    FINALIZANDO...
                  </>
                ) : (
                  "SALVAR VENDA"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
